import { FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { tiposHoraHooks } from './hooks';
import { tipoHoraInputSchema, type TipoHoraFormValues } from './schemas';
import { HORA_TIPOS, type TipoHoraDto } from './types';

export interface TipoHoraFormDialogProps {
  open: boolean;
  tipoHora: TipoHoraDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: TipoHoraFormValues = {
  nome: '',
  sigla: '',
  multiplicadorHora: 1,
  tipo: 'PAD',
  ativo: true,
};

export function TipoHoraFormDialog({
  open,
  tipoHora,
  onClose,
}: TipoHoraFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    TipoHoraFormValues,
    TipoHoraDto,
    TipoHoraFormValues,
    number
  >({
    open,
    entity: tipoHora,
    schema: tipoHoraInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (th) => ({
      nome: th.nome,
      sigla: th.sigla,
      multiplicadorHora: th.multiplicadorHora,
      tipo: th.tipo,
      ativo: th.ativo,
    }),
    getId: (th) => th.id,
    hooks: { useCreate: tiposHoraHooks.useCreate, useUpdate: tiposHoraHooks.useUpdate },
    messages: {
      created: t('features.tiposHora.toasts.created'),
      updated: t('features.tiposHora.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={isEdit ? t('features.tiposHora.editTitle') : t('features.tiposHora.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
    >
      <Stack spacing={2}>
        <TextField
          label={t('features.tiposHora.fields.nome')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.nome)}
          helperText={form.formState.errors.nome?.message}
          {...form.register('nome')}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('features.tiposHora.fields.sigla')}
            fullWidth
            inputProps={{ maxLength: 10 }}
            error={Boolean(form.formState.errors.sigla)}
            helperText={form.formState.errors.sigla?.message}
            {...form.register('sigla')}
          />
          <Controller
            control={form.control}
            name="tipo"
            render={({ field, fieldState }) => (
              <TextField
                select
                fullWidth
                label={t('features.tiposHora.fields.tipo')}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              >
                {HORA_TIPOS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
        <Controller
          control={form.control}
          name="multiplicadorHora"
          render={({ field, fieldState }) => (
            <TextField
              fullWidth
              type="number"
              label={t('features.tiposHora.fields.multiplicadorHora')}
              helperText={
                fieldState.error?.message ?? t('features.tiposHora.fields.multiplicadorHoraHelper')
              }
              error={Boolean(fieldState.error)}
              inputProps={{ step: '0.0001', min: 0 }}
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value))}
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        />
        <Controller
          control={form.control}
          name="ativo"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? true}
                  onChange={(_, checked) => field.onChange(checked)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.tiposHora.fields.ativo')}
            />
          )}
        />
      </Stack>
    </FormDialog>
  );
}
